/**
 * F37 (Wave 2D) — admin review-moderation detail page.
 *
 * Route: `/review-moderation/:id`
 * Permission: `reviews:moderate`.
 *
 * Shows the TRUE-identity review (player + trainer real names, even when the
 * review is anonymous-on-display — D3), the flag context, and a moderation
 * timeline derived from the review's own moderation fields (flagged / removed /
 * locked). The Moderate CTA opens the ModerateReviewDialog
 * (hide/remove/restore/edit/lock + mandatory reason).
 *
 * Composes F02 primitives only (Card / Chip / StatusChip / Banner / Button /
 * Skeleton / EmptyState / AuditLogTimeline).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  type AuditEvent,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  useToast,
} from '@bravemove/ui-web';
import type { AdminModerateReviewInput, ReviewStatus, TrainerReviewAdmin } from '@bravemove/types';
import { adminRecommendationApi } from '../../api/recommendation';
import { translateApiError } from '../../auth/errors';
import { ModerateReviewDialog } from '../../components/recommendation/ModerateReviewDialog';
import { InertTick } from '../../components/verification/EntityVerificationCard';

const REVIEW_STATUS_CHIP: Record<ReviewStatus, StatusChipStatus> = {
  published: 'approved',
  flagged: 'in_review',
  removed: 'rejected',
};

function ratingStars(rating: number): string {
  return '★'.repeat(Math.max(0, Math.min(5, rating))).padEnd(5, '☆');
}

/** Build a moderation-history timeline from the review's own moderation fields. */
function buildTimeline(r: TrainerReviewAdmin, t: ReturnType<typeof useI18n>['t']): AuditEvent[] {
  const events: AuditEvent[] = [
    {
      id: 'created',
      at: r.createdAt,
      kind: 'create',
      summary: t('admin:recommendation.moderation.timeline.created', {
        defaultValue: 'Review submitted',
      }),
      actor: { name: r.playerDisplayName },
    },
  ];
  if (r.flaggedAt) {
    events.push({
      id: 'flagged',
      at: r.flaggedAt,
      kind: 'alert',
      summary: t('admin:recommendation.moderation.timeline.flagged', {
        defaultValue: 'Flagged for moderation',
      }),
      actor: { name: r.trainerDisplayName },
      ...(r.flagReason ? { details: r.flagReason } : {}),
    });
  }
  if (r.removedByAdminId) {
    events.push({
      id: 'removed',
      at: r.updatedAt,
      kind: 'delete',
      summary: t('admin:recommendation.moderation.timeline.removed', {
        defaultValue: 'Removed by admin',
      }),
      actor: { name: t('admin:recommendation.moderation.admin_actor', { defaultValue: 'Admin' }) },
      ...(r.removedReason ? { details: r.removedReason } : {}),
    });
  }
  if (r.adminLocked) {
    events.push({
      id: 'locked',
      at: r.updatedAt,
      kind: 'permission',
      summary: t('admin:recommendation.moderation.timeline.locked', {
        defaultValue: 'Locked from further edits',
      }),
      actor: { name: t('admin:recommendation.moderation.admin_actor', { defaultValue: 'Admin' }) },
    });
  }
  // newest-first
  return events.sort((a, b) => b.at.localeCompare(a.at));
}

export function ReviewModerationDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ readonly id: string }>();

  const [review, setReview] = useState<TrainerReviewAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moderateOpen, setModerateOpen] = useState(false);
  const [moderateError, setModerateError] = useState<string | null>(null);

  const refresh = useCallback(
    async (reviewId: string) => {
      setLoading(true);
      setError(null);
      try {
        const resp = await adminRecommendationApi.reviewDetail(reviewId);
        if (resp === null) setNotFound(true);
        else setReview(resp);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!id) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh(id);
  }, [id, refresh]);

  const timeline = useMemo(() => (review ? buildTimeline(review, t) : []), [review, t]);

  if (!id) return <Navigate to="/review-moderation" replace />;

  const handleModerate = async (input: AdminModerateReviewInput) => {
    if (!review) return;
    setModerateError(null);
    try {
      const next = await adminRecommendationApi.moderateReview(review.id, input);
      setReview(next);
      setModerateOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:recommendation.moderation.applied', { defaultValue: 'Action applied' }),
      });
    } catch (err) {
      setModerateError(translateApiError(err, t));
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl flex flex-col gap-4" data-testid="review-moderation-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (notFound || !review) {
    return (
      <div className="max-w-3xl">
        <EmptyState
          title={t('recommendation:error.REVIEW_NOT_FOUND', { defaultValue: 'Review not found.' })}
          action={
            <Button variant="ghost" onClick={() => navigate('/review-moderation')}>
              {t('admin:recommendation.moderation.back', { defaultValue: 'Back to queue' })}
            </Button>
          }
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl">
        <Banner tone="danger">{error}</Banner>
      </div>
    );
  }

  return (
    <div className="max-w-3xl flex flex-col gap-4" data-testid="review-moderation-detail-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="text-[12px] text-accent hover:underline self-start focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40 rounded"
            onClick={() => navigate('/review-moderation')}
          >
            {t('admin:recommendation.moderation.back', { defaultValue: 'Back to queue' })}
          </button>
          <h1 className="text-h1 font-bold text-ink">
            {t('recommendation:moderation.review_detail_title', { defaultValue: 'Review' })}
          </h1>
        </div>
        <StatusChip
          size="sm"
          status={REVIEW_STATUS_CHIP[review.status] ?? 'pending'}
          pulse={review.status === 'flagged'}
          label={t(`recommendation:moderation.status.${review.status}`, {
            defaultValue: review.status,
          })}
        />
      </header>

      {/* Review card — TRUE identity */}
      <Card>
        <div className="flex flex-col gap-3" data-testid="review-detail-card">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-mono text-[15px] text-warning tabular-nums"
              aria-label={`${review.rating}/5`}
            >
              {ratingStars(review.rating)}
            </span>
            {review.anonymous ? (
              <Chip>
                {t('admin:recommendation.moderation.anonymous_display', {
                  defaultValue: 'Anonymous on display',
                })}
              </Chip>
            ) : null}
          </div>

          <dl className="grid grid-cols-[140px_1fr] gap-y-2 gap-x-3 text-[12.5px]">
            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:recommendation.moderation.player', { defaultValue: 'Player' })}
            </dt>
            <dd className="inline-flex flex-wrap items-center gap-1.5 text-ink">
              {review.playerDisplayName}
              {/* F09 v2 — the TRUE author's player tick, inert (shown even for
                  anonymous reviews: admins always see real identity, F37 D3). */}
              <InertTick
                verified={review.authorVerified}
                entityType="player"
                testId="review-detail-author-tick"
              />
              <span className="ms-2 font-mono text-[11px] text-ink-3">{review.playerUserId}</span>
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:recommendation.moderation.trainer', { defaultValue: 'Trainer' })}
            </dt>
            <dd className="inline-flex flex-wrap items-center gap-1.5 text-ink">
              {review.trainerDisplayName}
              <InertTick
                verified={review.trainerVerified}
                entityType="trainer"
                testId="review-detail-trainer-tick"
              />
              <span className="ms-2 font-mono text-[11px] text-ink-3">{review.trainerUserId}</span>
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:recommendation.moderation.sport', { defaultValue: 'Sport' })}
            </dt>
            <dd className="text-ink capitalize">{review.sportName.replace(/-/g, ' ')}</dd>

            {review.tags.length > 0 ? (
              <>
                <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                  {t('admin:recommendation.moderation.tags', { defaultValue: 'Tags' })}
                </dt>
                <dd className="flex flex-wrap gap-1.5">
                  {review.tags.map((tag) => (
                    <Chip key={tag}>{tag}</Chip>
                  ))}
                </dd>
              </>
            ) : null}
          </dl>

          {review.body ? (
            <div className="border-t border-hairline pt-3">
              <span className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('admin:recommendation.moderation.body', { defaultValue: 'Review text' })}
              </span>
              <p className="mt-1 text-[13px] text-ink leading-relaxed whitespace-pre-wrap">
                {review.body}
              </p>
            </div>
          ) : null}

          {review.flagReason ? (
            <Banner tone="warning">
              <span className="font-medium">
                {t('admin:recommendation.moderation.flag_reason', { defaultValue: 'Flag reason' })}:
              </span>{' '}
              {review.flagReason}
            </Banner>
          ) : null}

          <div className="border-t border-hairline pt-3 flex items-center justify-end">
            <Button
              variant="primary"
              onClick={() => {
                setModerateError(null);
                setModerateOpen(true);
              }}
              data-testid="review-moderate-cta"
            >
              {t('admin:recommendation.moderation.moderate_cta', { defaultValue: 'Moderate' })}
            </Button>
          </div>
        </div>
      </Card>

      {/* Moderation history */}
      <Card>
        <h2 className="mb-3 text-h2 font-bold text-ink">
          {t('admin:recommendation.moderation.history_title', {
            defaultValue: 'Moderation history',
          })}
        </h2>
        <AuditLogTimeline events={timeline} />
      </Card>

      <ModerateReviewDialog
        open={moderateOpen}
        review={review}
        onConfirm={handleModerate}
        onClose={() => setModerateOpen(false)}
        errorMessage={moderateError}
      />
    </div>
  );
}

export default ReviewModerationDetailPage;
